import React from "react";
import { compose } from "helper/compose";
import { withTranslation, type WithTranslation } from "react-i18next";

import { isAllowed } from "helper/acl";
import NotFoundPage from "../Common/NotFoundPage";
import Loader from "../Common/Loader";
import PageHeader from "../Common/PageHeader";
import ListPager from "../Common/ListPager";
import SuperPopin from "../Common/SuperPopin";
import DocumentLibraryApi from "api/DocumentLibraryApi";
import LogApi from "api/LogApi";
import {
  getDocumentLibrary,
  getDocumentLibraryByRoute,
  getDocumentLibraryGroupByParent,
} from "helper/documentModules";
import { withModulesData, type ModulesQueryProps } from "api/queries/useModules";
import { normalizeLanguage } from "config/i18n";
import DocumentLibraryFilter, {
  type DocumentLibraryAvailableFilters,
  type DocumentLibraryFilters,
} from "./DocumentLibraryFilter";
import DocumentLibraryList, { type DocumentLibraryItem } from "./DocumentLibraryList";
import DocumentLibraryDetailDocument from "./DocumentLibraryDetailDocument";
import type { UserData } from "../../types/state";
import { withAuthData, type AuthInjectedProps } from "../../context/AuthContext";
import { getApiData, getApiErrorCode } from "../../types/api";

const NotFoundPageAny = NotFoundPage as unknown as React.ComponentType<Record<string, unknown>>;
const ListPagerAny = ListPager as unknown as React.ComponentType<Record<string, unknown>>;
const SuperPopinAny = SuperPopin as unknown as React.ComponentType<Record<string, unknown>>;
const DocumentLibraryFilterAny = DocumentLibraryFilter as unknown as React.ComponentType<
  Record<string, unknown>
>;
const DocumentLibraryListAny = DocumentLibraryList as unknown as React.ComponentType<
  Record<string, unknown>
>;
const DocumentLibraryDetailDocumentAny =
  DocumentLibraryDetailDocument as unknown as React.ComponentType<Record<string, unknown>>;

interface Library {
  routePath?: string;
  title?: string;
  titleKey?: string;
  descriptionKey?: string;
  parentSlug?: string;
  slug?: string;
  frontPermission?: string;
}

interface Pagination {
  page: number;
  perPage: number;
  total: number;
  pageCount: number;
  limitChoices: number[];
}

const defaultFilters: DocumentLibraryFilters = {
  search: "",
  year: "",
  format: "",
};

const defaultPagination: Pagination = {
  page: 1,
  perPage: 10,
  total: 0,
  pageCount: 1,
  limitChoices: [10, 20, 50],
};

const defaultAvailableFilters: DocumentLibraryAvailableFilters = {
  years: [],
  formats: [],
};

function downloadBytes(data: Uint8Array, filename = "document"): void {
  const bytes = new ArrayBuffer(data.byteLength);
  new Uint8Array(bytes).set(data);
  const blob = new Blob([bytes], { type: "application/octet-stream" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

interface DocumentLibraryPageOwnProps extends WithTranslation {
  params: { parentSlug?: string; moduleSlug?: string };
  location?: { hash?: string };
}

interface DocumentLibraryPageStateProps {
  userData: UserData;
}

type DocumentLibraryPageProps = DocumentLibraryPageOwnProps &
  DocumentLibraryPageStateProps &
  ModulesQueryProps &
  AuthInjectedProps;

function DocumentLibraryPage(props: DocumentLibraryPageProps) {
  const {
    t,
    i18n,
    params,
    location,
    modules,
    modulesLoaded,
    userData,
    adminToken,
    sessionActions,
  } = props;

  const currentLanguageKey =
    normalizeLanguage(i18n?.resolvedLanguage || userData?.language || "fr") || "fr";

  const getLibrary = React.useCallback((): Library | null => {
    return (
      (getDocumentLibraryByRoute(
        modules,
        params.parentSlug,
        params.moduleSlug
      ) as unknown as Library | null) ||
      (getDocumentLibrary(modules, params.moduleSlug) as unknown as Library | null) ||
      null
    );
  }, [modules, params.parentSlug, params.moduleSlug]);

  const [isLoading, setIsLoading] = React.useState(true);
  const [items, setItems] = React.useState<DocumentLibraryItem[]>([]);
  const [filters, setFilters] = React.useState<DocumentLibraryFilters>({ ...defaultFilters });
  const [pagination, setPagination] = React.useState<Pagination>({ ...defaultPagination });
  const [availableFilters, setAvailableFilters] = React.useState<DocumentLibraryAvailableFilters>({
    ...defaultAvailableFilters,
  });
  const [languageKey, setLanguageKey] = React.useState<string>(currentLanguageKey);
  const [errorCode, setErrorCode] = React.useState<string | null>(null);
  const [previewItem, setPreviewItem] = React.useState<DocumentLibraryItem | null>(null);

  const logLibrary = React.useCallback(
    (slug: string) => {
      LogApi.createOne("module_" + slug, typeof adminToken === "string" ? adminToken : null).catch(
        () => undefined
      );
    },
    [adminToken]
  );

  const loadLibrary = React.useCallback(() => {
    const library = getLibrary();
    if (!library) {
      setIsLoading(false);
      setErrorCode("DOCUMENT_LIBRARY_NOT_FOUND");
      return;
    }

    if (!isAllowed(library.frontPermission)) {
      setIsLoading(false);
      setErrorCode("FORBIDDEN");
      return;
    }

    setIsLoading(true);
    setErrorCode(null);

    const requestFilters: Record<string, unknown> = {
      ...filters,
      page: pagination.page,
      pagesize: pagination.perPage,
    };

    DocumentLibraryApi.getLibrary(
      library as unknown as { parentSlug: string; entrySlug: string },
      requestFilters,
      languageKey || currentLanguageKey
    )
      .then((response) => {
        const content = getApiData(response) as
          | {
              pagination?: Record<string, unknown>;
              available_filters?: DocumentLibraryAvailableFilters;
            }
          | undefined;
        const paginationResp = (content?.pagination || {}) as Record<string, unknown>;
        const nextPage = parseInt(String(paginationResp.page), 10);
        const nextPerPage = parseInt(String(paginationResp.perPage ?? paginationResp.limit), 10);
        const nextPageCount = parseInt(String(paginationResp.pageCount), 10);
        const nextTotal = parseInt(String(paginationResp.total), 10);

        setIsLoading(false);
        setItems((paginationResp.result as DocumentLibraryItem[]) || []);
        setAvailableFilters(content?.available_filters || defaultAvailableFilters);
        setPagination((prev) => ({
          page: Number.isNaN(nextPage) ? prev.page : nextPage,
          total: Number.isNaN(nextTotal) ? 0 : nextTotal,
          perPage: Number.isNaN(nextPerPage) ? prev.perPage : nextPerPage,
          pageCount: Number.isNaN(nextPageCount) ? 1 : Math.max(1, nextPageCount),
          limitChoices: (paginationResp.limit_choices as number[]) || prev.limitChoices,
        }));
      })
      .catch((error) => {
        if (getApiErrorCode(error) === "NOT_LOGGED") {
          sessionActions.logOutUser();
          return;
        }

        setIsLoading(false);
        setErrorCode(getApiErrorCode(error) || "DOCUMENT_LIBRARY_NOT_FOUND");
        setItems([]);
      });
  }, [
    getLibrary,
    filters,
    pagination.page,
    pagination.perPage,
    languageKey,
    currentLanguageKey,
    sessionActions,
  ]);

  // Mount + reload on slug / modules / language / user-id change
  const slugKey = `${params.parentSlug}|${params.moduleSlug}`;
  const userId = userData && userData.id;
  React.useEffect(() => {
    const library = getLibrary();
    if (library?.slug) {
      logLibrary(library.slug);
    }
    if (library || modulesLoaded) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      loadLibrary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slugKey, modulesLoaded, userId, languageKey]);

  // Language change tracking
  React.useEffect(() => {
    if (currentLanguageKey !== languageKey) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLanguageKey(currentLanguageKey);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPagination((prev) => ({ ...prev, page: 1 }));
      setPreviewItem(null);
    }
  }, [currentLanguageKey, languageKey]);

  // Reload on filter / pagination change (after first mount)
  const didMountRef = React.useRef(false);
  React.useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    loadLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.perPage, filters.search, filters.year, filters.format]);

  React.useEffect(() => {
    const hash = String(location?.hash || "");
    const targetId = hash.replace(/^#document-/, "");
    if (isLoading || targetId === "" || !hash.startsWith("#document-")) {
      return;
    }

    document.getElementById(`document-${targetId}`)?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [isLoading, items, location?.hash]);

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }) as DocumentLibraryFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters({ ...defaultFilters });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > pagination.pageCount) return;
    setPagination((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const perPage = parseInt(event.target.value, 10);
    setPagination((prev) => ({
      ...prev,
      perPage: Number.isNaN(perPage) ? 10 : perPage,
      page: 1,
    }));
  };

  const handleDownload = (documentId: number | string) => {
    const itemLanguage = items.find((item) => item.id === documentId)?.lang;

    DocumentLibraryApi.getFile(documentId, itemLanguage || currentLanguageKey)
      .then((response) => {
        const content = getApiData(response, {}) as {
          download_url?: string;
          filedata?: string;
          filename?: string;
        };
        if (content.download_url) {
          window.open(content.download_url, "_blank", "noopener,noreferrer");
          return;
        }

        downloadBytes(
          Uint8Array.from(atob(content.filedata || ""), (char) => char.charCodeAt(0)),
          content.filename
        );
      })
      .catch((error) => {
        if (getApiErrorCode(error) === "NOT_LOGGED") {
          sessionActions.logOutUser();
        }
      });
  };

  const handlePreview = (item: DocumentLibraryItem) => {
    setPreviewItem(item);
  };

  const closePreview = () => {
    setPreviewItem(null);
  };

  const renderToolbar = () => {
    return (
      <ListPagerAny
        className="DocumentLibraryFooter DocumentLibraryFooter--top"
        page={pagination.page}
        pageNumber={pagination.pageCount}
        pageSize={pagination.perPage}
        pageSizeId="documentLibraryPageSizeTop"
        pageSizeOptions={pagination.limitChoices}
        summary={t("document_library.results", {
          count: pagination.total || 0,
        })}
        changePage={handlePageChange}
        changePageSize={handlePageSizeChange}
      />
    );
  };

  const renderForbidden = (library: Library) => {
    return (
      <div className="Page DocumentLibraryPage">
        <div className="Page-content DocumentLibraryPage-content">
          <section className="DocumentLibraryState DocumentLibraryState--forbidden">
            <p className="DocumentLibraryState-kicker">{t("profile.forbidden_kicker")}</p>
            <h1 className="DocumentLibraryState-title">{t("common.forbiden")}</h1>
            <p className="DocumentLibraryState-text">
              {t("document_library.forbidden", {
                module: library.title || t(library.titleKey || ""),
              })}
            </p>
          </section>
        </div>
      </div>
    );
  };

  const getBreadcrumbs = (library: Library) => {
    const group = getDocumentLibraryGroupByParent(modules, library.parentSlug) as {
      titleKey: string;
      libraries: Array<{ routePath?: string }>;
    } | null;
    const firstLibrary = group && group.libraries.length > 0 ? group.libraries[0] : null;

    return [
      { label: t("navigation.label_home"), to: BASE_PATH + "/" },
      group
        ? { label: t(group.titleKey), to: firstLibrary ? firstLibrary.routePath : undefined }
        : null,
      { label: library.title || (library.titleKey ? t(library.titleKey) : "") },
    ].filter(Boolean) as Array<{ label: string; to?: string }>;
  };

  const library = getLibrary();

  if (!modulesLoaded && !library) {
    return (
      <div className="Page DocumentLibraryPage">
        <div className="Page-content DocumentLibraryPage-content">
          <Loader />
        </div>
      </div>
    );
  }

  if (!library || errorCode === "DOCUMENT_LIBRARY_NOT_FOUND") {
    return <NotFoundPageAny />;
  }

  if (!isAllowed(library.frontPermission) || errorCode === "FORBIDDEN") {
    return renderForbidden(library);
  }

  return (
    <div className="Page DocumentLibraryPage">
      <div className="Page-content DocumentLibraryPage-content">
        <PageHeader
          breadcrumbs={getBreadcrumbs(library)}
          title={library.title || (library.titleKey ? t(library.titleKey) : "")}
          className="PageHero--moduleTitle"
        />

        <div className="DocumentLibraryLayout tools-layout">
          <aside className="DocumentLibrarySidebar">
            <DocumentLibraryFilterAny
              filters={filters}
              availableFilters={availableFilters}
              onChange={handleFilterChange}
              onReset={handleResetFilters}
            />
          </aside>

          <div className="DocumentLibraryMain">
            {renderToolbar()}
            <DocumentLibraryListAny
              isLoading={isLoading}
              items={items}
              onDownload={handleDownload}
              onPreview={handlePreview}
            />
            <ListPagerAny
              className="DocumentLibraryFooter DocumentLibraryFooter--bottom"
              page={pagination.page}
              pageNumber={pagination.pageCount}
              pageSize={pagination.perPage}
              pageSizeId="documentLibraryPageSizeBottom"
              pageSizeOptions={pagination.limitChoices}
              changePage={handlePageChange}
              changePageSize={handlePageSizeChange}
            />
          </div>
        </div>
        <SuperPopinAny
          shown={!!previewItem}
          closePopin={closePreview}
          additionalClasses={["DocumentPreviewPopin", "isBig"]}
        >
          {previewItem ? (
            <DocumentLibraryDetailDocumentAny
              embedded
              library={library}
              document={previewItem}
              documentId={previewItem.id}
            />
          ) : (
            <div />
          )}
        </SuperPopinAny>
      </div>
    </div>
  );
}

export default compose(withTranslation(), withAuthData, withModulesData)(DocumentLibraryPage);
